if (!customElements.get('absen-featured-hero')) {
  customElements.define('absen-featured-hero', class extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.pages = [...this.querySelectorAll('[data-page]')];
      if (this.slides.length < 2) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.current = 0;
      this.elapsed = 0;
      this.duration = Number(document.querySelector('[data-ah2-hero]')?.dataset.delay || this.dataset.duration) || 6000;
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      this.manualPaused = this.motionPreference.matches;
      this.explicitPlay = false;
      this.inView = false;
      this.hovered = false;
      this.focused = false;
      this.pauseButton = this.querySelector('[data-pause]');
      this.dialog = document.querySelector('#quick-add-dialog dialog');

      this.querySelector('[data-previous]').addEventListener('click', () => this.goTo(this.current - 1), { signal });
      this.querySelector('[data-next]').addEventListener('click', () => this.goTo(this.current + 1), { signal });
      this.pages.forEach((page, index) => page.addEventListener('click', () => this.goTo(index), { signal }));
      this.pauseButton.addEventListener('click', () => {
        this.manualPaused = !this.manualPaused;
        this.explicitPlay = !this.manualPaused;
        this.syncPlayback();
      }, { signal });
      this.addEventListener('mouseenter', () => { this.hovered = true; this.explicitPlay = false; this.syncPlayback(); }, { signal });
      this.addEventListener('mouseleave', () => { this.hovered = false; this.explicitPlay = false; this.syncPlayback(); }, { signal });
      this.addEventListener('focusin', (event) => { this.focused = true; if (event.target !== this.pauseButton) this.explicitPlay = false; this.syncPlayback(); }, { signal });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) { this.focused = false; this.explicitPlay = false; this.syncPlayback(); }
      }, { signal });
      document.addEventListener('visibilitychange', () => this.syncPlayback(), { signal });
      this.motionPreference.addEventListener('change', (event) => {
        this.manualPaused = event.matches;
        this.syncPlayback();
      }, { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target.closest('[data-slide]'));
        if (index >= 0) { this.manualPaused = true; this.goTo(index, false); this.syncPlayback(); }
      }, { signal });
      this.track.addEventListener('scroll', () => {
        if (this.programmatic) return;
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => {
          const index = Math.min(this.slides.length - 1, Math.round(Math.abs(this.track.scrollLeft) / this.track.clientWidth));
          if (index !== this.current) this.update(index);
        }, 80);
      }, { passive: true, signal });

      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.syncPlayback();
      }, { threshold: 0.15 });
      this.visibilityObserver.observe(this);
      if (this.dialog) {
        this.dialogObserver = new MutationObserver(() => this.syncPlayback());
        this.dialogObserver.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
      }
      this.update(0);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.visibilityObserver?.disconnect();
      this.dialogObserver?.disconnect();
      this.progressAnimation?.cancel();
      clearTimeout(this.advanceTimer);
      clearTimeout(this.scrollTimer);
      clearTimeout(this.programmaticTimer);
    }

    get isPlaying() {
      return !this.manualPaused && (this.explicitPlay || (!this.hovered && !this.focused)) && this.inView && !document.hidden && !this.dialog?.open;
    }

    syncPlayback() {
      const shouldPlay = this.isPlaying;
      if (shouldPlay !== this.playing) {
        if (shouldPlay) {
          this.startedAt = performance.now();
          this.progressAnimation.play();
          this.advanceTimer = setTimeout(() => this.goTo(this.current + 1), Math.max(0, this.duration - this.elapsed));
        } else {
          if (this.playing) this.elapsed += performance.now() - this.startedAt;
          this.progressAnimation.pause();
          clearTimeout(this.advanceTimer);
        }
        this.playing = shouldPlay;
      }
      this.toggleAttribute('data-paused', !shouldPlay);
      this.pauseButton.setAttribute('aria-pressed', String(this.manualPaused));
      this.pauseButton.setAttribute('aria-label', this.manualPaused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
    }

    goTo(index, smooth = true) {
      const target = (index + this.slides.length) % this.slides.length;
      const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      this.programmatic = true;
      clearTimeout(this.programmaticTimer);
      this.track.scrollTo({ left: target * this.track.clientWidth * direction, behavior: smooth && !this.motionPreference.matches ? 'smooth' : 'instant' });
      this.update(target);
      this.programmaticTimer = setTimeout(() => { this.programmatic = false; }, smooth ? 700 : 0);
    }

    update(index) {
      this.current = index;
      this.elapsed = 0;
      this.playing = false;
      clearTimeout(this.advanceTimer);
      this.progressAnimation?.cancel();
      this.pages.forEach((page, pageIndex) => {
        page.setAttribute('aria-current', String(pageIndex === index));
        page.toggleAttribute('data-complete', pageIndex < index);
      });
      this.progressAnimation = this.pages[index].querySelector('.absen-featured-hero__page-progress').animate(
        [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
        { duration: this.duration, fill: 'forwards' }
      );
      this.progressAnimation.pause();
      this.syncPlayback();
    }
  });
}
